import { useCallback, useEffect, useState, type RefObject } from 'react';

export type FitSquareGridSpec =
  | { mode: 'fixed'; cols: number; rows: number }
  | { mode: 'auto'; itemCount: number };

export type FitSquareGridSizeOptions = {
  gap?: number;
  padding?: number;
  minTileSize?: number;
  bottomPadding?: number;
};

export type FitSquareGridLayout = {
  tileSize: number;
  cols: number;
  rows: number;
};

function getViewportHeight() {
  return window.visualViewport?.height ?? window.innerHeight;
}

export function getViewportAvailableHeight(element: HTMLElement, bottomPadding = 16) {
  const top = element.getBoundingClientRect().top;
  return Math.max(0, Math.floor(getViewportHeight() - top - bottomPadding));
}

export function computeFitSquareGridLayout(
  width: number,
  height: number,
  spec: FitSquareGridSpec,
  options: FitSquareGridSizeOptions = {}
): FitSquareGridLayout {
  const gap = options.gap ?? 8;
  const padding = options.padding ?? 0;
  const minTileSize = options.minTileSize ?? 56;

  const availableWidth = Math.max(0, width - padding * 2);
  const availableHeight = Math.max(0, height - padding * 2);

  const layoutFor = (cols: number, rows: number): FitSquareGridLayout => {
    const safeCols = Math.max(1, cols);
    const safeRows = Math.max(1, rows);

    // Höhe ist der primäre begrenzende Faktor.
    let tileSize = Math.max(
      minTileSize,
      Math.floor((availableHeight - gap * (safeRows - 1)) / safeRows)
    );

    const requiredWidth = safeCols * tileSize + gap * (safeCols - 1);
    if (requiredWidth > availableWidth) {
      const fromWidth = Math.floor((availableWidth - gap * (safeCols - 1)) / safeCols);
      tileSize = Math.max(minTileSize, Math.min(tileSize, fromWidth));
    }

    return { tileSize, cols: safeCols, rows: safeRows };
  };

  if (spec.mode === 'fixed') {
    return layoutFor(spec.cols, spec.rows);
  }

  const count = Math.max(1, spec.itemCount);
  let best = layoutFor(1, count);

  // Zeilen zuerst variieren, damit die Höhe die Kachelgröße bestimmt.
  for (let rows = 1; rows <= count; rows += 1) {
    const cols = Math.ceil(count / rows);
    const candidate = layoutFor(cols, rows);
    if (candidate.tileSize > best.tileSize) {
      best = candidate;
    }
  }

  return best;
}

export function useFitSquareGridSize(
  containerRef: RefObject<HTMLElement | null>,
  spec: FitSquareGridSpec | null,
  options: FitSquareGridSizeOptions = {}
) {
  const gap = options.gap ?? 8;
  const padding = options.padding ?? 0;
  const minTileSize = options.minTileSize ?? 56;
  const bottomPadding = options.bottomPadding ?? 16;

  const [layout, setLayout] = useState<FitSquareGridLayout>({
    tileSize: minTileSize,
    cols: 1,
    rows: 1
  });

  const recompute = useCallback(() => {
    const element = containerRef.current;
    if (!element || !spec) {
      return;
    }

    const { width } = element.getBoundingClientRect();
    const height = getViewportAvailableHeight(element, bottomPadding);
    if (width <= 0 || height <= 0) {
      return;
    }

    setLayout(
      computeFitSquareGridLayout(width, height, spec, {
        gap,
        padding,
        minTileSize
      })
    );
  }, [containerRef, spec, gap, padding, minTileSize, bottomPadding]);

  useEffect(() => {
    recompute();

    const element = containerRef.current;
    if (!element) {
      return undefined;
    }

    const observer = new ResizeObserver(recompute);
    observer.observe(element);
    window.addEventListener('resize', recompute);
    window.addEventListener('scroll', recompute, { passive: true });
    window.visualViewport?.addEventListener('resize', recompute);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', recompute);
      window.removeEventListener('scroll', recompute);
      window.visualViewport?.removeEventListener('resize', recompute);
    };
  }, [containerRef, recompute]);

  return layout;
}

export function useViewportRemainingHeight(
  anchorRef: RefObject<HTMLElement | null>,
  minHeight = 320,
  bottomPadding = 16
) {
  const [height, setHeight] = useState<number | null>(null);

  useEffect(() => {
    const update = () => {
      const element = anchorRef.current;
      if (!element) {
        return;
      }

      const top = element.getBoundingClientRect().top;
      setHeight(Math.max(minHeight, Math.floor(getViewportHeight() - top - bottomPadding)));
    };

    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, { passive: true });
    window.visualViewport?.addEventListener('resize', update);

    const element = anchorRef.current;
    const observer = element ? new ResizeObserver(update) : null;
    if (element && observer) {
      observer.observe(element);
    }

    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update);
      window.visualViewport?.removeEventListener('resize', update);
      observer?.disconnect();
    };
  }, [anchorRef, minHeight, bottomPadding]);

  return height;
}
